import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api, { discoverAPI, clientAPI } from '../../utils/api';
import { formatDate } from '../../utils/timeFormat';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { ImageLightbox } from '../../components/ImageLightbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import CertificationList from '../../components/CertificationList';
import CertificationBadges from '../../components/CertificationBadges';
import GgfBadge from '../../components/GgfBadge';
import { resolveProfilePhoto } from '../../utils/profilePhoto';
import {
  ArrowLeft, Star, Award, MapPin, Calendar, Briefcase,
  Instagram, User, Shield, Loader2, MessageSquare, Heart, 
  Plus, CheckCircle2, AlertCircle, Crown, Users, X, BadgeCheck, Ban,
} from 'lucide-react';
import { toast } from 'sonner';
import AddToTeamModal from '../../components/AddToTeamModal';
import TalentReviewsBadge from '../../components/TalentReviewsBadge';
import { useAuth } from '../../context/AuthContext';

import { formatDateMonthDay } from '../../utils/timeFormat';
const TalentProfileDetail = () => {
  const navigate = useNavigate();
  const { talentId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  // Iter-252 — GGF visibility context (admin / GGF client / own profile)
  const isAdmin = ['admin', 'super_admin'].includes((user?.role || '').toLowerCase());
  const isGgfClient = /\b(guardian garage floors|ggf)\b/i.test(`${user?.company_name || ''} ${user?.name || ''}`);
  // Iter-253 — Creator self-preview at /talent/me/preview-creator.
  // When no :talentId is in the URL, the talent is previewing their OWN profile
  // exactly as a brand sees it. Resolve to user.id.
  const effectiveTalentId = talentId || user?.id;
  const isSelfPreview = !talentId && user?.role === 'talent';
  const isOwnProfile = isSelfPreview || user?.id === talentId;
  // Iter-227m — `?source=ai_match` from SmartSuggestionsWidget tags the
  // view as ai_recommendation_view. Default surface is direct_link.
  const surfaceTag = searchParams.get('source') === 'ai_match'
    ? 'ai_match'
    : 'direct_link';
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  
  // Book from Profile state
  const [showBookModal, setShowBookModal] = useState(false);
  const [clientGigs, setClientGigs] = useState([]);
  const [selectedGigId, setSelectedGigId] = useState('');
  const [booking, setBooking] = useState(false);
  const [loadingGigs, setLoadingGigs] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  // 2026-06 — Client Block Talent
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [blockReason, setBlockReason] = useState('not_a_fit');
  const [blockText, setBlockText] = useState('');
  const [blockBusy, setBlockBusy] = useState(false);
  const [showAddToTeam, setShowAddToTeam] = useState(false);
  // Iter-221 — Expand the portfolio grid beyond the initial 4 thumbs so
  // clients can see ALL photos a talent has uploaded (was hidden behind
  // a non-functional "View More" button).
  const [portfolioExpanded, setPortfolioExpanded] = useState(false);

  useEffect(() => {
    if (effectiveTalentId) fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveTalentId]);

  const fetchProfile = async () => {
    try {
      const res = await discoverAPI.getTalentProfile(effectiveTalentId);
      setProfile(res.data);
      setIsFavorite(res.data.is_favorite || false);
      // Iter-227m — direct-link / AI-recommendation profile view tracking.
      // Skip tracking when a talent is previewing themselves (Iter-253).
      if (!isSelfPreview) {
        api.post('/metrics/profile-view', {
          talent_id: effectiveTalentId,
          source: surfaceTag,
          surface: surfaceTag,
        }).catch(() => {});
      }
    } catch {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const toggleFavorite = async () => {
    setFavoriteLoading(true);
    try {
      const res = await api.post(`/client/favorites/toggle?talent_id=${talentId}`);
      setIsFavorite(res.data.is_favorite);
      toast.success(res.data.message);
    } catch {
      toast.error('Failed to update favorite');
    } finally {
      setFavoriteLoading(false);
    }
  };

  // Check block status (client/agency only, real talent view).
  useEffect(() => {
    if (!talentId || !(user?.role === 'client' || user?.role === 'agency')) return;
    api.get(`/client/blocked-talent/check/${talentId}`)
      .then((r) => setIsBlocked(!!r.data?.blocked))
      .catch(() => {});
  }, [talentId, user]);

  const submitBlock = async () => {
    setBlockBusy(true);
    try {
      await api.post('/client/blocked-talent', {
        talent_id: talentId,
        reason_code: blockReason,
        reason_text: blockText.trim() || null,
      });
      setIsBlocked(true);
      setBlockOpen(false);
      setBlockText('');
      toast.success('Talent blocked — they won\'t appear in your recommendations or future opportunities.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to block talent');
    } finally {
      setBlockBusy(false);
    }
  };

  const unblock = async () => {
    setBlockBusy(true);
    try {
      await api.delete(`/client/blocked-talent/${talentId}`);
      setIsBlocked(false);
      toast.success('Talent unblocked');
    } catch {
      toast.error('Failed to unblock');
    } finally {
      setBlockBusy(false);
    }
  };

  const fetchClientGigs = async () => {
    setLoadingGigs(true);
    try {
      const res = await clientAPI.getShifts();
      // Filter to only open gigs that aren't full
      const openGigs = res.data.filter(gig => 
        gig.status === 'open' && gig.bookings_count < gig.talent_needed
      );
      setClientGigs(openGigs);
    } catch (e) {
      toast.error('Failed to load your gigs');
    } finally {
      setLoadingGigs(false);
    }
  };

  const handleOpenBookModal = () => {
    fetchClientGigs();
    setShowBookModal(true);
  };

  const handleBookTalent = async () => {
    if (!selectedGigId) {
      toast.error('Please select a gig');
      return;
    }
    
    setBooking(true);
    try {
      await clientAPI.bookTalent({
        shift_id: selectedGigId,
        talent_id: talentId
      });
      toast.success(`${displayName} has been booked!`);
      setShowBookModal(false);
      setSelectedGigId('');
    } catch (e) {
      const errorMsg = e.response?.data?.detail || 'Failed to book talent';
      toast.error(errorMsg);
    } finally {
      setBooking(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="profile-not-found">
        <div className="text-center">
          <User className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Profile not found</p>
          <Button variant="outline" onClick={() => navigate(-1)} className="mt-4">Go Back</Button>
        </div>
      </div>
    );
  }

  // Safely access profile fields with defaults
  const displayName = profile.display_name || profile.first_name || 'Unnamed';
  const ageRange = profile.age_range || null;
  const city = profile.city || 'Unknown';
  const state = profile.state || '';
  const roleName = profile.role_name || null;
  const avgRating = profile.avg_rating || 0;
  const ratingCount = profile.rating_count || 0;
  const completedGigs = profile.completed_gigs || 0;
  const memberSince = profile.member_since || null;
  const isPremium = profile.is_premium || false;
  const performanceTier = profile.performance_tier || null;
  const trustLine = profile.trust_line || null;
  const socialBadges = profile.social_badges || [];
  const bio = profile.bio || null;
  const languages = profile.languages || null;
  const portfolioImages = profile.portfolio_images || [];
  const profilePhotoUrl = resolveProfilePhoto(profile);
  const displayPhoto = imgError ? (portfolioImages.length > 0 ? portfolioImages[0] : null) : profilePhotoUrl;
  // Combined, de-duplicated photo set for the navigable lightbox.
  const lightboxPhotos = [];
  const _seenPhoto = new Set();
  [displayPhoto, ...portfolioImages].forEach((u) => { if (u && !_seenPhoto.has(u)) { lightboxPhotos.push(u); _seenPhoto.add(u); } });
  const openLightbox = (url) => {
    const i = lightboxPhotos.indexOf(url);
    setLightboxIdx(i >= 0 ? i : 0);
  };
  const recentReviews = profile.recent_reviews || [];
  const nicheCategory = profile.niche_category || null;
  const instagramHandle = profile.instagram_handle || null;
  const tiktokHandle = profile.tiktok_handle || null;
  const followerCount = profile.follower_count || 0;

  const attributes = profile.attributes || {};
  const certifications = profile.certifications || [];
  const multidayCompleted = profile.multiday_completed || 0;
  const reliabilityPercent = profile.reliability_percent || 0;
  const workExperience = profile.work_experience || [];

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star key={i} className={`w-4 h-4 ${i < Math.round(rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-200'}`} />
    ));
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="talent-profile-detail">
      {/* Iter-253 — Self-preview banner for talents viewing their own
          public profile via /talent/me/preview-creator. */}
      {isSelfPreview && (
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white" data-testid="creator-self-preview-banner">
          <div className="max-w-4xl mx-auto px-4 py-2 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs sm:text-sm font-semibold flex items-center gap-2">
              <span className="text-base">👀</span>
              <span>Preview Mode — this is exactly what brands see</span>
            </div>
            <button
              onClick={() => navigate('/talent/creator-network')}
              className="text-xs underline text-white/90 hover:text-white"
              data-testid="creator-self-preview-back"
            >
              ← Back to Creator Network
            </button>
          </div>
        </div>
      )}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-button">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h1 className="text-lg font-semibold text-gray-900">
              {isSelfPreview ? 'Creator Profile Preview' : 'Talent Profile'}
            </h1>
          </div>
          {/* Hide booking CTA in self-preview */}
          {!isSelfPreview && (
            <div className="flex items-center gap-2">
              {(user?.role === 'client' || user?.role === 'agency') && (
                <Button
                  variant="outline"
                  onClick={() => navigate(`/client/messages/talent/${talentId}`)}
                  className="border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                  data-testid="message-talent-btn"
                >
                  <MessageSquare className="w-4 h-4 mr-1.5" />
                  Message
                </Button>
              )}
              {(user?.role === 'client' || user?.role === 'agency') && (
                isBlocked ? (
                  <Button
                    variant="outline"
                    onClick={unblock}
                    disabled={blockBusy}
                    className="border-rose-300 text-rose-700 hover:bg-rose-50"
                    data-testid="unblock-talent-btn"
                  >
                    <Ban className="w-4 h-4 mr-1.5" /> Unblock
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    onClick={() => setBlockOpen(true)}
                    className="border-slate-300 text-slate-600 hover:bg-rose-50 hover:text-rose-700 hover:border-rose-300"
                    data-testid="block-talent-btn"
                  >
                    <Ban className="w-4 h-4 mr-1.5" /> Block
                  </Button>
                )
              )}
              <Button
                onClick={handleOpenBookModal}
                className="bg-cyan-500 hover:bg-cyan-600 text-white"
                data-testid="book-from-profile-btn"
              >
                <Plus className="w-4 h-4 mr-1.5" />
                Book for Gig
              </Button>
            </div>
          )}
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Left Column - Photo & Quick Info */}
          <div className="space-y-4">
            <div className="relative">
              {/* Premium badge — positioned over card, outside overflow-hidden */}
              {isPremium && (
                <Badge className="absolute top-4 right-4 z-30 premium-badge-gold bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 text-gray-900 border-0 shadow-[0_2px_16px_rgba(245,158,11,0.5)] font-bold text-xs px-3 py-1.5 rounded-full" data-testid="premium-badge">
                  <Crown className="w-3.5 h-3.5 mr-1" />Premium Talent
                </Badge>
              )}
              <Card className={`overflow-hidden ${isPremium ? 'premium-halo' : ''}`}>
                {/* Premium glow overlay */}
                {isPremium && <div className="absolute inset-0 rounded-lg premium-glow pointer-events-none z-10" />}
                <div className="relative">
                  {displayPhoto ? (
                    <img
                      src={displayPhoto}
                      alt={displayName}
                      className={`w-full h-72 object-cover cursor-zoom-in ${isPremium ? 'border-b-[3px] border-pink-400' : ''}`}
                      onError={() => setImgError(true)}
                      onClick={() => openLightbox(displayPhoto)}
                      data-testid="talent-main-photo"
                    />
                  ) : (
                    <div className="w-full h-72 bg-gradient-to-br from-cyan-100 to-cyan-50 flex items-center justify-center">
                      <User className="w-20 h-20 text-cyan-300" />
                    </div>
                  )}
                </div>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-1">
                  <h2 className="text-xl font-bold text-gray-900" data-testid="profile-name">{displayName}</h2>
                  {/* Iter-253 — Hide client-only actions when a talent is self-previewing. */}
                  {!isSelfPreview && (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setShowAddToTeam(true)}
                        className="p-1.5 rounded-full text-gray-300 hover:text-violet-500 transition-all"
                        data-testid="add-to-team-btn"
                        title="Add to Team"
                      >
                        <Users className="w-5 h-5" />
                      </button>
                      <button
                        onClick={toggleFavorite}
                        disabled={favoriteLoading}
                        className={`p-1.5 rounded-full transition-all ${isFavorite ? 'text-pink-500 hover:text-pink-600' : 'text-gray-300 hover:text-pink-400'}`}
                        data-testid="favorite-toggle"
                        title={isFavorite ? 'Remove from favorites' : 'Save to favorites'}
                      >
                        <Heart className={`w-5 h-5 ${isFavorite ? 'fill-pink-500' : ''}`} />
                      </button>
                    </div>
                  )}
                </div>
                {ageRange && <p className="text-sm text-gray-500 mb-2">{ageRange}</p>}
                <div className="flex items-center gap-1.5 text-sm text-gray-600 mb-3">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <span>{city}{state ? `, ${state}` : ''}</span>
                </div>
                {roleName && (
                  <Badge className="bg-cyan-50 text-cyan-700 border-cyan-200">{roleName}</Badge>
                )}
                {/* Iter-252 — Creator Type badge (visible only to admins viewing
                    creator-flagged talent, and to clients on the Creator Network surface) */}
                {profile.creator_type && profile.creator_type !== 'creator' && (
                  <Badge
                    className={`ml-1 border-0 text-[10px] font-bold ${
                      profile.creator_type === 'both'
                        ? 'bg-gradient-to-r from-violet-500 to-pink-500 text-white'
                        : profile.creator_type === 'influencer'
                        ? 'bg-pink-500 text-white'
                        : 'bg-purple-500 text-white'
                    }`}
                    data-testid="creator-type-badge"
                  >
                    {profile.creator_type === 'both' ? 'Influencer + Creator' :
                     profile.creator_type === 'influencer' ? 'Influencer' : 'Content Creator'}
                  </Badge>
                )}
                {performanceTier && (
                  <Badge
                    className={`ml-1 border-0 text-[10px] font-bold ${
                      performanceTier.key === 'elite' ? 'bg-gradient-to-r from-amber-500 to-yellow-400 text-amber-900' :
                      performanceTier.key === 'trending' ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white' :
                      performanceTier.key === 'active' ? 'bg-cyan-500 text-white' :
                      'bg-gray-400 text-white'
                    }`}
                    data-testid="performance-tier-badge"
                  >
                    {performanceTier.key === 'elite' ? 'Top Performer' :
                     performanceTier.key === 'trending' ? 'Trending' :
                     performanceTier.key === 'active' ? 'Active' : ''}
                  </Badge>
                )}
                {socialBadges.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {socialBadges.map(b => (
                      <Badge key={b.id} className={`text-[9px] font-semibold ${
                        b.color === 'pink' ? 'bg-pink-100 text-pink-700' :
                        b.color === 'orange' ? 'bg-orange-100 text-orange-700' :
                        b.color === 'cyan' ? 'bg-cyan-100 text-cyan-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`} data-testid={`social-badge-${b.id}`}>
                        {b.label}
                      </Badge>
                    ))}
                  </div>
                )}
                {/* Iter-252 — Guardian Garage Floors badge in Certifications row
                    Only visible to admins / own profile / Guardian Garage Floors clients */}
                {profile.ggf_certified && (
                  <div className="mt-2" data-testid="profile-ggf-section">
                    <GgfBadge
                      certified
                      score={profile.ggf_test_score}
                      date={profile.ggf_certified_at}
                      source={profile.ggf_certified_source}
                      size="md"
                      showDate
                      context={{ isAdmin, isGgfClient, isOwnProfile }}
                    />
                  </div>
                )}
                {trustLine && (
                  <p className="text-[11px] text-gray-500 mt-1" data-testid="trust-line">{trustLine}</p>
                )}
              </CardContent>
            </Card>
            </div>

            {/* Rating Card — only show when at least one review exists. */}
            {ratingCount > 0 ? (
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex">{renderStars(avgRating)}</div>
                    <span className="text-lg font-bold text-gray-900" data-testid="profile-avg-rating">{avgRating.toFixed(1)}</span>
                  </div>
                  <p className="text-xs text-gray-500" data-testid="profile-rating-count">
                    {ratingCount} review{ratingCount !== 1 ? 's' : ''}
                  </p>
                  <div className="mt-3 flex items-center gap-4 text-sm">
                    <div>
                      <span className="font-semibold text-gray-900">{completedGigs}</span>
                      <span className="text-gray-500 ml-1">gig{completedGigs !== 1 ? 's' : ''}</span>
                    </div>
                    {memberSince && (
                      <div className="text-gray-500">
                        Since {new Date(memberSince).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-4">
                  <p className="text-xs text-gray-500" data-testid="profile-no-ratings">
                    No reviews yet
                  </p>
                  <div className="mt-3 flex items-center gap-4 text-sm">
                    <div>
                      <span className="font-semibold text-gray-900">{completedGigs}</span>
                      <span className="text-gray-500 ml-1">gig{completedGigs !== 1 ? 's' : ''}</span>
                    </div>
                    {memberSince && (
                      <div className="text-gray-500">
                        Since {new Date(memberSince).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

          </div>

          {/* Right Column - Bio, Photo Grid, Attributes, Stats, Reviews */}
          <div className="md:col-span-2 space-y-4">
            {/* Iter-313i — Roles this talent works. Merges the discover
                `gig_roles` (predefined) with `custom_roles` (Other Role
                Not Listed) so clients see the full skill picture. */}
            {(() => {
              const gig = Array.isArray(profile.gig_roles) ? profile.gig_roles : [];
              const custom = Array.isArray(profile.custom_roles) ? profile.custom_roles : [];
              const all = [...new Set([...gig, ...custom].map((r) => String(r).trim()).filter(Boolean))];
              if (all.length === 0) return null;
              return (
                <Card data-testid="talent-roles-section">
                  <CardContent className="p-5">
                    <h3 className="text-sm font-medium text-gray-700 mb-3">Roles</h3>
                    <div className="flex flex-wrap gap-2">
                      {all.map((role) => {
                        const isCustom = custom.includes(role) && !gig.includes(role);
                        return (
                          <Badge
                            key={role}
                            variant="outline"
                            className={
                              isCustom
                                ? 'bg-violet-50 text-violet-700 border-violet-200 text-xs py-1 px-2.5'
                                : 'bg-cyan-50 text-cyan-700 border-cyan-200 text-xs py-1 px-2.5'
                            }
                            data-testid={`talent-role-badge-${role.toLowerCase().replace(/\s+/g, '-')}`}
                            title={isCustom ? 'Custom role added by this talent' : 'Predefined role'}
                          >
                            {role}
                            {isCustom && <span className="ml-1 text-[9px] uppercase tracking-wider opacity-80">custom</span>}
                          </Badge>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              );
            })()}

            {/* Bio */}
            {bio && (
              <Card>
                <CardContent className="p-5">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">About</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{bio}</p>
                </CardContent>
              </Card>
            )}

            {/* Photo Grid */}
            {portfolioImages.length > 0 && (
              <Card data-testid="photo-grid">
                <CardContent className="p-5">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">
                    Photos {portfolioImages.length > 1 && (
                      <span className="text-gray-400 font-normal">({portfolioImages.length})</span>
                    )}
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {(portfolioExpanded ? portfolioImages : portfolioImages.slice(0, 4)).map((img, i) => (
                      <img
                        key={i}
                        src={img}
                        alt={`Photo ${i + 1}`}
                        className="w-full h-36 object-cover rounded-lg cursor-zoom-in hover:opacity-90 transition"
                        onClick={() => openLightbox(img)}
                        data-testid={`portfolio-photo-${i}`}
                        loading="lazy"
                      />
                    ))}
                  </div>
                  {portfolioImages.length > 4 && (
                    <button
                      type="button"
                      onClick={() => setPortfolioExpanded(v => !v)}
                      className="mt-2 text-xs text-cyan-600 hover:text-cyan-700 font-medium"
                      data-testid="view-more-photos"
                    >
                      {portfolioExpanded
                        ? 'Show less'
                        : `View More (${portfolioImages.length - 4} more)`}
                    </button>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Attribute Pills */}
            {Object.keys(attributes).length > 0 && (
              <Card data-testid="attribute-pills">
                <CardContent className="p-5">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Quick Info</h3>
                  <div className="flex flex-wrap gap-2">
                    {attributes.height && (
                      <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200 text-xs py-1 px-2.5">
                        Height: {attributes.height}
                      </Badge>
                    )}
                    {attributes.shirt_size && (
                      <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200 text-xs py-1 px-2.5">
                        Shirt: {attributes.shirt_size}
                      </Badge>
                    )}
                    {attributes.shoe_size && (
                      <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200 text-xs py-1 px-2.5">
                        Shoe: {attributes.shoe_size}
                      </Badge>
                    )}
                    {attributes.hair_color && (
                      <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200 text-xs py-1 px-2.5">
                        Hair: {attributes.hair_color}
                      </Badge>
                    )}
                    {attributes.eye_color && (
                      <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200 text-xs py-1 px-2.5">
                        Eyes: {attributes.eye_color}
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Performance & Experience Stats */}
            <Card data-testid="performance-stats">
              <CardContent className="p-5">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Performance</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="text-center p-3 bg-cyan-50 rounded-lg">
                    <p className="text-2xl font-bold text-cyan-700">{completedGigs}</p>
                    <p className="text-[11px] text-gray-500">Shifts Done</p>
                  </div>
                  <div className="text-center p-3 bg-violet-50 rounded-lg">
                    <p className="text-2xl font-bold text-violet-700">{multidayCompleted}</p>
                    <p className="text-[11px] text-gray-500">Multi-Day</p>
                  </div>
                  <div className="text-center p-3 bg-emerald-50 rounded-lg">
                    <p className="text-2xl font-bold text-emerald-700">{reliabilityPercent}%</p>
                    <p className="text-[11px] text-gray-500">Reliability</p>
                  </div>
                  <div className="text-center p-3 bg-amber-50 rounded-lg">
                    <p className="text-2xl font-bold text-amber-700">{certifications.length}</p>
                    <p className="text-[11px] text-gray-500">Certifications</p>
                  </div>
                </div>
                {/* Iter-202 — verified certifications with status/expiration.
                    Clients see name + status + expiration (no doc link). */}
                <div className="mt-4" data-testid="client-talent-certifications">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
                    Certifications
                  </h3>
                  <CertificationList
                    certifications={certifications}
                    variant="full"
                    showDocLink={false}
                    testIdPrefix="client-talent-cert"
                    emptyMessage="No certifications uploaded yet."
                  />
                </div>

                {/* Iter-225h — assessment-based training certifications (Phase 1B/1C).
                    Shown with score + earned/expires dates so clients can vet
                    that the talent has actually passed iEvntür assessments. */}
                {Array.isArray(profile.training_certifications) && profile.training_certifications.length > 0 && (
                  <div className="mt-4" data-testid="client-talent-training-certs">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-emerald-700 mb-2 flex items-center gap-1.5">
                      <BadgeCheck className="w-3.5 h-3.5" />
                      iEvntür Assessments
                    </h3>
                    <CertificationBadges
                      certifications={profile.training_certifications}
                      variant="detailed"
                      showScore
                      showDate
                      testIdPrefix="client-talent-training-cert"
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Work Experience / Brands */}
            {workExperience.length > 0 && (
              <Card data-testid="work-experience">
                <CardContent className="p-5">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Experience</h3>
                  <div className="flex flex-wrap gap-2">
                    {workExperience.map((exp, i) => (
                      <Badge key={i} variant="outline" className="bg-slate-50 text-slate-700 border-slate-200 text-xs">
                        {typeof exp === 'string' ? exp : exp.brand || exp.name || exp.title || ''}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Go Premium CTA (only shown for non-premium talent viewed by client) */}
            {!isPremium && (
              <Card className="border-amber-200 bg-gradient-to-r from-amber-50 to-yellow-50" data-testid="go-premium-cta">
                <CardContent className="p-5 text-center">
                  <Crown className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                  <h3 className="font-semibold text-gray-900 mb-1">Not a Premium Talent yet</h3>
                  <p className="text-sm text-gray-600 mb-3">Premium Talent get more visibility and appear higher in search results.</p>
                </CardContent>
              </Card>
            )}

            {/* Languages */}
            {languages && (
              <Card>
                <CardContent className="p-5">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Languages</h3>
                  <p className="text-sm text-gray-600">{languages}</p>
                </CardContent>
              </Card>
            )}

            {/* Niche / Category */}
            {nicheCategory && (
              <Card>
                <CardContent className="p-5">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Specialization</h3>
                  <Badge variant="outline">{nicheCategory}</Badge>
                </CardContent>
              </Card>
            )}

            {/* Iter-225v — Premium Client Feedback panel (Client → Talent reviews) */}
            <TalentReviewsBadge talentId={talentId} variant="detail" limit={5} />

            {/* Legacy compact reviews list (kept as fallback when the new endpoint has no data) */}
            {recentReviews.length > 0 && false && (
              <Card>
                <CardContent className="p-5">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Recent Reviews</h3>
                  <div className="space-y-3">
                    {recentReviews.map((review, i) => (
                      <div key={i} className="border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="flex">{renderStars(review.rating || 0)}</div>
                          {review.date && (
                            <span className="text-[10px] text-gray-400">
                              {formatDate(review.date)}
                            </span>
                          )}
                        </div>
                        {review.feedback && <p className="text-sm text-gray-600">{review.feedback}</p>}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Social */}
            {(instagramHandle || tiktokHandle) && (
              <Card>
                <CardContent className="p-5 space-y-2">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Social</h3>
                  {instagramHandle && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Instagram className="w-4 h-4 text-pink-500" />
                      <span>@{instagramHandle}</span>
                    </div>
                  )}
                  {tiktokHandle && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <span className="w-4 h-4 text-center font-bold text-xs">TT</span>
                      <span>@{tiktokHandle}</span>
                    </div>
                  )}
                  {followerCount > 0 && (
                    <p className="text-xs text-gray-500 mt-1">{followerCount.toLocaleString()} followers</p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Empty State */}
            {!bio && portfolioImages.length === 0 && recentReviews.length === 0 && completedGigs === 0 && (
              <Card className="border-dashed border-gray-300">
                <CardContent className="p-8 text-center">
                  <User className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">This talent hasn't added more details yet.</p>
                  <p className="text-xs text-gray-400 mt-1">Check back later for portfolio and reviews.</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Book from Profile Modal */}
      <Dialog open={showBookModal} onOpenChange={setShowBookModal}>
        <DialogContent className="bg-white max-w-md" data-testid="book-modal">
          <DialogHeader>
            <DialogTitle>Book {displayName}</DialogTitle>
            <DialogDescription>
              Select a gig to book this talent for
            </DialogDescription>
          </DialogHeader>
          
          {loadingGigs ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-cyan-500" />
            </div>
          ) : clientGigs.length === 0 ? (
            <div className="py-6 text-center">
              <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500 mb-4">You don't have any open gigs with available spots.</p>
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowBookModal(false);
                  navigate('/client/shifts/create');
                }}
              >
                <Plus className="w-4 h-4 mr-1.5" />
                Create a New Gig
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label className="text-sm text-gray-700">Select Gig</Label>
                <Select value={selectedGigId} onValueChange={setSelectedGigId}>
                  <SelectTrigger className="mt-1.5" data-testid="gig-select">
                    <SelectValue placeholder="Choose a gig..." />
                  </SelectTrigger>
                  <SelectContent className="bg-white max-h-60">
                    {clientGigs.map(gig => (
                      <SelectItem key={gig.id} value={gig.id}>
                        <div className="flex flex-col">
                          <span className="font-medium">{gig.venue_name || gig.role_name}</span>
                          <span className="text-xs text-gray-500">
                            {gig.city}, {gig.state} - {formatDateMonthDay(gig.shift_date)}
                            {' '} ({gig.bookings_count}/{gig.talent_needed} booked)
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {selectedGigId && (
                <div className="bg-cyan-50 rounded-lg p-3 border border-cyan-100">
                  <p className="text-xs text-cyan-700">
                    <CheckCircle2 className="w-3.5 h-3.5 inline mr-1" />
                    This talent will be notified and added to the gig
                  </p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBookModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleBookTalent}
              disabled={!selectedGigId || booking || clientGigs.length === 0}
              className="bg-cyan-500 hover:bg-cyan-600"
              data-testid="confirm-booking-btn"
            >
              {booking ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Booking...</>
              ) : (
                <><CheckCircle2 className="w-4 h-4 mr-1.5" />Confirm Booking</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AddToTeamModal
        talentId={talentId}
        talentName={displayName}
        isOpen={showAddToTeam}
        onClose={() => setShowAddToTeam(false)}
      />

      <Dialog open={blockOpen} onOpenChange={setBlockOpen}>
        <DialogContent data-testid="block-talent-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Ban className="w-5 h-5 text-rose-500" /> Block {displayName}?</DialogTitle>
            <DialogDescription>
              They won't appear in your recommendations, discovery, or future opportunities.
              They are never notified. You can unblock anytime.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Reason</Label>
              <Select value={blockReason} onValueChange={setBlockReason}>
                <SelectTrigger data-testid="block-reason-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="not_a_fit">Not a fit</SelectItem>
                  <SelectItem value="poor_performance">Poor performance</SelectItem>
                  <SelectItem value="reliability">Reliability concerns</SelectItem>
                  <SelectItem value="conduct">Conduct</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Notes (optional, admin-visible)</Label>
              <Textarea value={blockText} onChange={(e) => setBlockText(e.target.value)} rows={2}
                placeholder="Add context for your team and admins…" data-testid="block-reason-text" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBlockOpen(false)} data-testid="block-cancel-btn">Cancel</Button>
            <Button onClick={submitBlock} disabled={blockBusy} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="block-confirm-btn">
              {blockBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Ban className="w-4 h-4 mr-2" />} Block Talent
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Photo Lightbox */}
      <ImageLightbox
        images={lightboxPhotos}
        index={lightboxIdx}
        onIndexChange={setLightboxIdx}
        onClose={() => setLightboxIdx(null)}
      />
    </div>
  );
};

export default TalentProfileDetail;
